/**
 * 0233 (04-11-2026): the site cash-flow group and line on a material, and the
 * System Administrator's approval of them.
 *
 * A NEW material is saved PENDING and cannot be used anywhere until Mrinmoy
 * Debnath (fn_is_superadmin) approves its group and line - as entered,
 * corrected, or sent back with a reason. A change asked for on an APPROVED
 * material waits the same way, but the material stays usable on its current
 * values meanwhile. The database enforces all of it (fn_material_cf_guard,
 * fn_material_must_be_approved, rpc_material_cf_review); this file only shows
 * it and offers the buttons.
 */
import { useEffect, useMemo, useState } from 'react'
import { CheckCircle2, Pencil, Undo2 } from 'lucide-react'
import { Card, Input, Select, useToast } from '@/components/ui'
import { dbMessage } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { dateTime } from '@/lib/format'

export interface CashflowLine { group_code: string; group_name: string; line_name: string; sort_order: number }

/** The 88 lines (A-R plus "Not on civil cash flow"), in blueprint order. */
export function useCashflowLines(enabled = true): CashflowLine[] {
  const [rows, setRows] = useState<CashflowLine[]>([])
  useEffect(() => {
    if (!enabled) return
    let live = true
    void supabase.from('material_cashflow_lines')
      .select('group_code, group_name, line_name, sort_order')
      .order('sort_order')
      .then(({ data }) => { if (live) setRows((data ?? []) as CashflowLine[]) })
    return () => { live = false }
  }, [enabled])
  return rows
}

/** True only for the System Administrator (the same rule as the database). */
export function useIsSuperadminFlag(): boolean {
  const [yes, setYes] = useState(false)
  useEffect(() => {
    let live = true
    void supabase.rpc('fn_is_superadmin').then(({ data }) => { if (live) setYes(data === true) })
    return () => { live = false }
  }, [])
  return yes
}

/** The small badge beside a material's name in the list. */
export function CfStatusPill({ m }: { m: any }) {
  if (m.cf_status === 'PENDING') {
    return <span className="pill-amber ml-1.5" title="New material - cannot be used until the System Administrator approves its cash-flow group and line.">Awaiting approval</span>
  }
  if (m.cf_status === 'REJECTED') {
    return <span className="pill-red ml-1.5" title={m.cf_review_note ?? ''}>Sent back</span>
  }
  if (m.cf_proposed_group) {
    return <span className="pill-amber ml-1.5" title={`Change waiting for approval: ${m.cf_proposed_group} / ${m.cf_proposed_line}. Still usable.`}>Cash-flow change waiting</span>
  }
  return null
}

function waitingKind(m: any): 'NEW' | 'REJECTED' | 'CHANGE' | null {
  if (m.cf_status === 'PENDING') return 'NEW'
  if (m.cf_status === 'REJECTED') return 'REJECTED'
  if (m.cf_proposed_group) return 'CHANGE'
  return null
}

/**
 * The card above the material list. The System Administrator approves,
 * corrects or sends back; everybody else who may see the list sees what is
 * waiting and why something was sent back, and Stores can open it to fix it.
 */
export function MaterialCashflowApprovals({ materials, isSuper, mayEdit, onChanged, onEdit }: {
  materials: any[]
  isSuper: boolean
  mayEdit: boolean
  onChanged: () => void
  onEdit: (m: any) => void
}) {
  const waiting = useMemo(
    () => materials.filter((m) => waitingKind(m) !== null)
      .sort((a, b) => String(a.cf_submitted_at ?? '').localeCompare(String(b.cf_submitted_at ?? ''))),
    [materials])
  const lines = useCashflowLines(waiting.length > 0)
  if (waiting.length === 0) return null

  const forMe = waiting.filter((m) => waitingKind(m) !== 'REJECTED').length
  return (
    <Card title={isSuper ? `Cash-flow approval - ${forMe} waiting for you` : `Materials waiting for cash-flow approval (${waiting.length})`}
          subtitle={isSuper
            ? 'A new material cannot be used until you approve its cash-flow group and line. Approve as entered, correct it, or send it back with a reason.'
            : 'Approved by the System Administrator. A new material cannot be used until then; a change on an existing material waits while the material stays usable.'}>
      <div className="table-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Material</th>
              <th>What is waiting</th>
              <th>Cash-flow group / line asked for</th>
              <th>Sent by</th>
              <th className="col-actions" />
            </tr>
          </thead>
          <tbody>
            {waiting.map((m) => (
              <ApprovalRow key={m.id} m={m} lines={lines} isSuper={isSuper} mayEdit={mayEdit}
                           onChanged={onChanged} onEdit={onEdit} />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

function ApprovalRow({ m, lines, isSuper, mayEdit, onChanged, onEdit }: {
  m: any; lines: CashflowLine[]; isSuper: boolean; mayEdit: boolean
  onChanged: () => void; onEdit: (m: any) => void
}) {
  const toast = useToast()
  const kind = waitingKind(m)!
  const askedG = kind === 'CHANGE' ? m.cf_proposed_group : m.cashflow_group
  const askedL = kind === 'CHANGE' ? m.cf_proposed_line : m.cashflow_line
  const [g, setG] = useState<string>(askedG ?? '')
  const [l, setL] = useState<string>(askedL ?? '')
  const [rejecting, setRejecting] = useState(false)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  const groups = [...new Set(lines.map((r) => r.group_name))].map((x) => ({ value: x, label: x }))
  const lineOpts = lines.filter((r) => r.group_name === g).map((r) => ({ value: r.line_name, label: r.line_name }))
  const corrected = g !== askedG || l !== askedL

  async function decide(decision: 'APPROVE' | 'REJECT') {
    if (decision === 'APPROVE' && (!g || !l)) { toast.error('Choose the cash-flow group and line.'); return }
    if (decision === 'REJECT' && !note.trim()) { toast.error('Give the reason - Stores will read it.'); return }
    setBusy(true)
    const { error } = await supabase.rpc('rpc_material_cf_review', {
      p_material_id: m.id, p_decision: decision,
      p_group: decision === 'APPROVE' && corrected ? g : null,
      p_line: decision === 'APPROVE' && corrected ? l : null,
      p_note: note.trim() || null,
    })
    setBusy(false)
    if (error) { toast.error(dbMessage(error, 'Could not record the decision.')); return }
    toast.success(decision === 'APPROVE'
      ? `${m.item_name} approved${corrected ? ' with your correction' : ''}.${kind === 'NEW' ? ' It can now be used.' : ''}`
      : `${m.item_name} sent back to Stores.`)
    onChanged()
  }

  return (
    <tr>
      <td className="text-sm">
        <div className="font-medium">{m.item_name}</div>
        <div className="font-mono text-xs text-slate-500">{m.id} · {m.item_code} · {m.category}</div>
      </td>
      <td className="text-xs">
        {kind === 'NEW' && <span className="pill-amber">New material - not usable yet</span>}
        {kind === 'CHANGE' && (
          <>
            <span className="pill-blue">Change on a usable material</span>
            <div className="mt-1 text-slate-500">Now: {m.cashflow_group ?? '(none)'} / {m.cashflow_line ?? '(none)'}</div>
          </>
        )}
        {kind === 'REJECTED' && (
          <>
            <span className="pill-red">Sent back - Stores to correct</span>
            {m.cf_review_note && <div className="mt-1 text-slate-600">Reason: {m.cf_review_note}</div>}
          </>
        )}
      </td>
      <td className="text-xs">
        {isSuper && kind !== 'REJECTED' ? (
          <div className="grid gap-1.5" style={{ minWidth: 260 }}>
            <Select value={g} options={groups} placeholder="Choose…"
                    onChange={(e) => {
                      const v = e.target.value
                      const ls = lines.filter((r) => r.group_name === v)
                      setG(v); setL(ls.length === 1 ? ls[0].line_name : '')
                    }} />
            <Select value={l} options={lineOpts} placeholder={g ? 'Choose…' : 'Pick a group first'}
                    disabled={!g} onChange={(e) => setL(e.target.value)} />
            {corrected && <div className="text-amber-700">Asked for: {askedG} / {askedL}</div>}
            {rejecting && (
              <Input value={note} onChange={(e) => setNote(e.target.value)}
                     placeholder="Reason for sending it back" autoFocus />
            )}
          </div>
        ) : (
          <span>{askedG} / {askedL}</span>
        )}
      </td>
      <td className="text-xs text-slate-500">{m.cf_submitted_at ? dateTime(m.cf_submitted_at) : '—'}</td>
      <td className="col-actions text-right">
        <div className="flex items-center justify-end gap-1">
          {isSuper && kind !== 'REJECTED' && !rejecting && (
            <>
              <button className="btn-primary btn-sm" disabled={busy} onClick={() => decide('APPROVE')}>
                <CheckCircle2 className="h-4 w-4" /> {corrected ? 'Correct & approve' : 'Approve'}
              </button>
              <button className="btn-secondary btn-sm" disabled={busy} onClick={() => setRejecting(true)}>
                <Undo2 className="h-4 w-4" /> Send back
              </button>
            </>
          )}
          {isSuper && rejecting && (
            <>
              <button className="btn-danger btn-sm" disabled={busy} onClick={() => decide('REJECT')}>Send back</button>
              <button className="btn-ghost btn-sm" disabled={busy} onClick={() => { setRejecting(false); setNote('') }}>Cancel</button>
            </>
          )}
          {kind === 'REJECTED' && mayEdit && (
            <button className="btn-secondary btn-sm" onClick={() => onEdit(m)} title="Correct it and save - it goes back for approval">
              <Pencil className="h-4 w-4" /> Correct
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}
